import { forwardRef, useCallback, useEffect, useImperativeHandle, useRef, useState } from "react";
import { useAppDispatch, useAppSelector } from "store";
import { addSnackbarMessage } from "store/slices/snackbar.store";
import { getExistingCallClient, useCallClient } from "./callClient";
import { CallClientId, CallProps, CallRef, PeerData, PointerData } from "./helpers";
import styles from "./RemoteCall.module.css";
import { useUserMedia } from "./useUserMedia";

const mediaConstraints = {
  audio: true,
  video: {
    // Prefer environment (back camera on phones) if available, otherwise will use "user"
    facingMode: "environment",
    width: 1920,
    height: 1080,
  },
};

export const RemoteShareCamera = forwardRef<CallRef, CallProps>(
  function RemoteShareCamera(props, ref) {
    const { peerId, onEnd } = props;
    const videoRef = useRef<HTMLVideoElement>(null);
    const captureCanvasRef = useRef<HTMLCanvasElement>(null);
    const [callActive, setCallActive] = useState<boolean>(false);
    const [pointer, setPointer] = useState<{ x: number; y: number }>();
    const { stream: myStream, error: cameraError } = useUserMedia(mediaConstraints);
    const dispatch = useAppDispatch();
    const { waitingFor } = useAppSelector((s) => s.remoteAccess);

    useImperativeHandle(ref, () => ({
      capture() {
        if (!videoRef.current || !captureCanvasRef.current) return;

        let canvas = captureCanvasRef.current;
        canvas.width = videoRef.current.videoWidth;
        canvas.height = videoRef.current.videoHeight;
        let context = canvas.getContext("2d");
        context?.drawImage(
          videoRef.current,
          0,
          0,
          videoRef.current.videoWidth,
          videoRef.current.videoHeight,
        );
        let data = canvas.toDataURL();
        return data;
      },
    }));

    useEffect(() => {
      if (cameraError) console.log(cameraError);
    }, [cameraError]);

    useEffect(() => {
      if (videoRef?.current && myStream) {
        videoRef.current.srcObject = myStream;
      }
    }, [myStream, videoRef]);

    useEffect(() => {
      console.log("onPeerReady", myStream);
      if (!myStream || !peerId) return;

      console.log("onPeerReady with stream");
      getExistingCallClient(CallClientId)?.then?.((client) => {
        if (client.currentCall) return;
        console.log("onPeerReady has client");
        client.connect(peerId, { metadata: { username: waitingFor } });
        client.startCall(peerId, myStream);
        setCallActive(true);
      });
    }, [myStream, peerId, waitingFor]);

    const onCallEnd = useCallback(() => {
      getExistingCallClient(CallClientId)?.then?.((client) => {
        client.endPeer();
      });
      setCallActive(false);
    }, []);

    const onDataReceived = useCallback(
      (data: unknown) => {
        console.log(data);
        const dataObj: PeerData = JSON.parse(data as string);

        if (dataObj.type === "pointer") {
          if (!videoRef.current) return;

          const { x: xp, y: yp } = dataObj.data as PointerData;
          let width = videoRef.current.clientWidth;
          let height = videoRef.current.clientHeight;

          setPointer({
            x: xp * width,
            y: yp * height,
          });
        } else if (dataObj.type === "info") {
          const commandData = dataObj.data as string;

          if (commandData === "endCall") {
            onEnd();
          } else if (commandData === "screenCapture") {
            //todo snackbar Screenshot was taken
            // might not work, worth trying first
            dispatch(addSnackbarMessage({ key: "RemoteAccess-ScreenshotTaken" }));
          }
        }
      },
      [dispatch, onEnd],
    );

    useCallClient(CallClientId, {
      clientOptions: { autoAnswerCall: false },
      debug: "RemoteAccessCall",
      onDataReceived,
      onCallEnded: onCallEnd,
      onPeerDestroyed: onCallEnd,
    });

    return (
      <>
        {pointer && (
          <div
            className={styles.callPointer}
            style={{
              transform: `translate(${pointer.x}px, ${pointer.y}px) translate(-50%, -50%)`,
            }}
          />
        )}
        <video
          style={{
            display: "block",
            maxWidth: "100%",
            objectFit: "contain",
          }}
          autoPlay
          muted={true}
          playsInline
          ref={videoRef}
        />
        <canvas ref={captureCanvasRef} style={{ display: "none" }} />
        {!callActive && (
          <div className={styles.noConnectionOverlay}>
            {/* TODO Waiting for call message plus end call/cancel button*/}
          </div>
        )}
        <canvas style={{ display: "none" }} />
      </>
    );
  },
);
